/**
 * Visual showcase of the Untitled UI theme components, for manual QA only —
 * not part of the product UI. Registered at /theme-demo (public route).
 */
import React, { useState } from "react";
import Button from "../Button/Button";
import Badge from "../Badge/Badge";
import IconButton from "../IconButton/IconButton";
import Checkbox from "../Checkbox/Checkbox";
import Radio from "../Radio/Radio";
import Toggle from "../Toggle/Toggle";
import Input from "../Input/Input";
import Select from "../Select/Select";
import ChipsInput from "../ChipsInput/ChipsInput";
import CalendarSelect from "../CalendarSelect/CalendarSelect";
import Avatar, { AvatarGroup } from "../Avatar/Avatar";
import Card from "../Card/Card";
import Table from "../Table/Table";
import ProgressMeter from "../ProgressMeter/ProgressMeter";
import ProgressSteps from "../ProgressSteps/ProgressSteps";
import RadioCardGrid from "../RadioCardGrid/RadioCardGrid";
import Tabs from "../Tabs/Tabs";
import Pagination from "../Pagination/Pagination";
import SidebarNav from "../SidebarNav/SidebarNav";
import DropdownMenu from "../DropdownMenu/DropdownMenu";
import Modal from "../Modal/Modal";
import Tooltip from "../Tooltip/Tooltip";
import EmptyState from "../EmptyState/EmptyState";
import { ToastProvider, useToast } from "../Toast/ToastProvider";
import Icon from "../icons/Icon";
import "../theme.scss";
import styles from "./ThemeDemo.module.scss";

const HIERARCHIES = ["primary", "secondaryGray", "tertiary", "destructive"];
const SIZES = ["sm", "md"];
const BADGE_COLORS = [
  "gray",
  "brand",
  "success",
  "warning",
  "error",
  "blueLight",
];
const ICON_BUTTON_VARIANTS = ["ghost", "outline"];

const MEMBERS = [
  {
    id: 1,
    name: "Maria Kotopoulou",
    email: "maria@company.com",
    status: "active",
    initials: "MK",
  },
  {
    id: 2,
    name: "Alex Chen",
    email: "alex@company.com",
    status: "pending",
    initials: "AC",
  },
  {
    id: 3,
    name: "Jordan Lee",
    email: "jordan@company.com",
    status: "active",
    initials: "JL",
  },
];

const CALENDAR_OPTIONS = [
  { uuid: "cal-1", label: "Team Events — maria@company.com", healthy: true },
  { uuid: "cal-2", label: "Personal — alex@company.com", healthy: true },
  { uuid: "cal-3", label: "Marketing — jordan@company.com", healthy: false },
];

const thumb = (color) =>
  `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="80"><rect width="120" height="80" fill="${color}"/></svg>`
  )}`;

const RADIO_CARD_ITEMS = [
  {
    key: "blank",
    name: "Blank calendar",
    pitch: "Start from scratch",
    tag: "Simple",
    thumbnailSrc: thumb("%23E9D7FE"),
  },
  {
    key: "detailed",
    name: "Detailed list",
    pitch: "Rich event cards",
    tag: "Popular",
    thumbnailSrc: thumb("%23D1FADF"),
  },
  {
    key: "grid",
    name: "Grid view",
    pitch: "Compact month grid",
    tag: "Classic",
    thumbnailSrc: thumb("%23FEF0C7"),
  },
  {
    key: "timeline",
    name: "Timeline",
    pitch: "Horizontal scroll",
    tag: "New",
    thumbnailSrc: thumb("%23FEE4E2"),
  },
];

const PROGRESS_STEPS_EXAMPLE = [
  { key: "template", status: "done", title: "Detailed list" },
  {
    key: "calendar",
    status: "current",
    title: "Choose calendar",
    subtitle: "Pick which calendar to publish to",
  },
  { key: "details", status: "upcoming", title: "Event details" },
  { key: "publish", status: "upcoming", title: "Publish" },
];

const Section = ({ title, children }) => (
  <section className={styles.section}>
    <h2 className={styles.sectionTitle}>{title}</h2>
    {children}
  </section>
);

const Row = ({ label, children }) => (
  <div className={styles.row}>
    <span className={styles.rowLabel}>{label}</span>
    <div className={styles.rowContent}>{children}</div>
  </div>
);

const ToastTriggerRow = () => {
  const showToast = useToast();
  return (
    <Row label="trigger">
      <Button
        hierarchy="secondaryGray"
        size="sm"
        onClick={() =>
          showToast({
            variant: "success",
            message: "Invitation resent to maria@company.com",
          })
        }
      >
        Show success toast
      </Button>
      <Button
        hierarchy="secondaryGray"
        size="sm"
        onClick={() =>
          showToast({ variant: "error", message: "Couldn't resend invitation" })
        }
      >
        Show error toast
      </Button>
      <Button
        hierarchy="secondaryGray"
        size="sm"
        onClick={() =>
          showToast({
            variant: "success",
            message: "Member removed",
            action: { label: "Undo", onClick: () => {} },
          })
        }
      >
        Show toast with action
      </Button>
    </Row>
  );
};

const ThemeDemoContent = () => {
  const [loadingKey, setLoadingKey] = useState(null);
  const [checkboxState, setCheckboxState] = useState({
    checked: true,
    indeterminate: false,
  });
  const [toggleOn, setToggleOn] = useState(true);
  const [radioValue, setRadioValue] = useState("viewer");
  const [inputValue, setInputValue] = useState("");
  const [chips, setChips] = useState(["maria@company.com", "alex@company.com"]);
  const [tableSelected, setTableSelected] = useState([]);
  const [tableSort, setTableSort] = useState({ key: "name", direction: "asc" });
  const [tableLoading, setTableLoading] = useState(false);
  const [tableError, setTableError] = useState(false);
  const [segmentedTab, setSegmentedTab] = useState("all");
  const [underlineTab, setUnderlineTab] = useState("overview");
  const [numberedPage, setNumberedPage] = useState(3);
  const [simplePage, setSimplePage] = useState(1);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [destructiveModalOpen, setDestructiveModalOpen] = useState(false);
  const [calendarValue, setCalendarValue] = useState("cal-1");
  const [radioCardValue, setRadioCardValue] = useState("detailed");

  const simulateLoading = (key) => {
    setLoadingKey(key);
    setTimeout(() => setLoadingKey(null), 1800);
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Untitled UI theme — component demo</h1>
        <p className={styles.subtitle}>
          react-app/src/apps/admin/theme/untitled — tokens at
          react-app/src/sass/untitled
        </p>
      </header>

      <Section title="Button">
        {HIERARCHIES.map((hierarchy) => (
          <Row key={hierarchy} label={hierarchy}>
            {SIZES.map((size) => (
              <Button key={size} hierarchy={hierarchy} size={size}>
                {size === "sm" ? "Small label" : "Medium label"}
              </Button>
            ))}
            <Button
              hierarchy={hierarchy}
              iconLeading={<Icon name="Settings01" />}
            >
              Leading icon
            </Button>
            <Button
              hierarchy={hierarchy}
              iconTrailing={<Icon name="ChevronDown" />}
            >
              Trailing icon
            </Button>
            <Button
              hierarchy={hierarchy}
              loading={loadingKey === hierarchy}
              onClick={() => simulateLoading(hierarchy)}
            >
              {loadingKey === hierarchy ? "Loading…" : "Click to load"}
            </Button>
            <Button hierarchy={hierarchy} disabled>
              Disabled
            </Button>
          </Row>
        ))}
        <Row label="fullWidth">
          <div style={{ width: 320 }}>
            <Button fullWidth>Full width primary</Button>
          </div>
        </Row>
      </Section>

      <Section title="IconButton">
        {ICON_BUTTON_VARIANTS.map((variant) => (
          <Row key={variant} label={variant}>
            {SIZES.map((size) => (
              <IconButton
                key={size}
                variant={variant}
                size={size}
                icon={<Icon name="Settings01" />}
                label="Settings"
              />
            ))}
            <IconButton
              variant={variant}
              icon={<Icon name="Trash01" />}
              label="Delete"
            />
            <IconButton
              variant={variant}
              icon={<Icon name="DotsVertical" />}
              label="More actions"
              disabled
            />
          </Row>
        ))}
      </Section>

      <Section title="Badge">
        <Row label="plain">
          {BADGE_COLORS.map((color) => (
            <Badge key={color} color={color}>
              {color}
            </Badge>
          ))}
        </Row>
        <Row label="dot">
          {BADGE_COLORS.map((color) => (
            <Badge key={color} color={color} dot>
              {color}
            </Badge>
          ))}
        </Row>
        <Row label="icon">
          <Badge color="success" icon={<Icon name="CheckCircle" />}>
            Verified
          </Badge>
          <Badge color="error" icon={<Icon name="AlertCircle" />}>
            Failed
          </Badge>
        </Row>
      </Section>

      <Section title="Checkbox / Radio / Toggle">
        <Row label="Checkbox">
          <Checkbox
            label="Checked"
            checked={checkboxState.checked}
            onChange={(e) =>
              setCheckboxState({
                checked: e.target.checked,
                indeterminate: false,
              })
            }
          />
          <Checkbox
            label="Indeterminate"
            checked={false}
            indeterminate
            readOnly
          />
          <Checkbox label="Disabled" disabled />
          <Checkbox
            label="With supporting text"
            supportingText="Applies to all future invoices"
          />
        </Row>
        <Row label="Radio">
          <Radio
            name="role"
            label="Viewer"
            checked={radioValue === "viewer"}
            onChange={() => setRadioValue("viewer")}
          />
          <Radio
            name="role"
            label="Editor"
            checked={radioValue === "editor"}
            onChange={() => setRadioValue("editor")}
          />
          <Radio name="role" label="Disabled" disabled />
        </Row>
        <Row label="Toggle">
          <Toggle
            size="sm"
            label="Email notifications"
            checked={toggleOn}
            onChange={setToggleOn}
          />
          <Toggle
            size="md"
            label="Email notifications"
            checked={toggleOn}
            onChange={setToggleOn}
          />
          <Toggle label="Disabled" disabled />
        </Row>
      </Section>

      <Section title="Input / Select / ChipsInput">
        <Row label="Input">
          <div style={{ width: 280 }}>
            <Input
              label="Email"
              placeholder="you@company.com"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              iconLeading={<Icon name="AtSign" />}
              hint="We'll never share your email."
            />
          </div>
          <div style={{ width: 280 }}>
            <Input
              label="Email"
              defaultValue="not-an-email"
              error="Enter a valid email address"
              required
            />
          </div>
          <div style={{ width: 200 }}>
            <Input label="Disabled" disabled placeholder="Can't edit" />
          </div>
        </Row>
        <Row label="Select">
          <div style={{ width: 200 }}>
            <Select
              options={[
                { value: "admin", label: "Admin" },
                { value: "editor", label: "Editor" },
                { value: "viewer", label: "Viewer" },
              ]}
            />
          </div>
          <div style={{ width: 200 }}>
            <Select size="lg" options={[{ value: "admin", label: "Admin" }]} />
          </div>
          <div style={{ width: 200 }}>
            <Select
              disabled
              options={[{ value: "owner", label: "Sole owner" }]}
            />
          </div>
        </Row>
        <Row label="ChipsInput">
          <div style={{ width: 360 }}>
            <ChipsInput
              label="Invite by email"
              placeholder="Add email and press Enter"
              value={chips}
              onChange={setChips}
            />
          </div>
        </Row>
        <Row label="CalendarSelect">
          <div style={{ width: 280 }}>
            <CalendarSelect
              label="Calendar"
              options={CALENDAR_OPTIONS}
              value={calendarValue}
              onChange={setCalendarValue}
              hint="The unhealthy dot marks a disconnected calendar."
            />
          </div>
          <div style={{ width: 240 }}>
            <CalendarSelect
              label="Disabled"
              options={CALENDAR_OPTIONS}
              disabled
              placeholder="Select a calendar…"
            />
          </div>
        </Row>
      </Section>

      <Section title="Avatar">
        <Row label="sizes">
          {["xs", "sm", "md", "lg"].map((size) => (
            <Avatar key={size} size={size} initials="MK" />
          ))}
        </Row>
        <Row label="status">
          <Avatar initials="MK" status="online" />
          <Avatar initials="AC" status="offline" />
          <Avatar />
        </Row>
        <Row label="group">
          <AvatarGroup
            avatars={[
              { initials: "MK" },
              { initials: "AC" },
              { initials: "JL" },
              { initials: "SP" },
              { initials: "TK" },
            ]}
            max={3}
          />
        </Row>
      </Section>

      <Section title="ProgressMeter">
        <Row label="normal">
          <ProgressMeter
            value={3}
            max={5}
            label="Seats used"
            secondaryText="3 of 5"
          />
        </Row>
        <Row label="warning">
          <ProgressMeter
            value={4}
            max={5}
            label="Seats used"
            secondaryText="4 of 5"
            note="You're close to your seat limit."
            noteVariant="warning"
            noteAction={{ label: "Add seats", onClick: () => {} }}
          />
        </Row>
        <Row label="error">
          <ProgressMeter
            value={5}
            max={5}
            label="Seats used"
            secondaryText="5 of 5"
            note="All seats are in use."
            noteVariant="error"
            noteAction={{ label: "Add seats", onClick: () => {} }}
          />
        </Row>
      </Section>

      <Section title="Card + Table">
        <Card>
          <Card.Header
            id="members-card-title"
            title="Members"
            subtitle="3 total"
          />
          <Card.Body noPadding>
            <Table
              columns={[
                {
                  key: "name",
                  header: "Name",
                  sortable: true,
                  render: (row) => (
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 12 }}
                    >
                      <Avatar size="sm" initials={row.initials} />
                      <span>{row.name}</span>
                    </div>
                  ),
                },
                { key: "email", header: "Email" },
                {
                  key: "status",
                  header: "Status",
                  render: (row) => (
                    <Badge
                      color={row.status === "active" ? "success" : "warning"}
                      dot
                    >
                      {row.status}
                    </Badge>
                  ),
                },
              ]}
              rows={tableLoading ? [] : tableError ? [] : MEMBERS}
              selectable
              selectedIds={tableSelected}
              onSelectionChange={setTableSelected}
              sortBy={tableSort}
              onSortChange={setTableSort}
              loading={tableLoading}
              error={tableError ? "Couldn't load members." : undefined}
              onRetry={() => setTableError(false)}
              emptyState={
                <EmptyState
                  title="No members yet"
                  body="Invite your team to start collaborating."
                />
              }
              renderActions={(row) => (
                <IconButton
                  size="sm"
                  icon={<Icon name="DotsVertical" />}
                  label={`More actions for ${row.name}`}
                />
              )}
            />
          </Card.Body>
          <Card.Footer>
            <span>{tableSelected.length} selected</span>
            <div style={{ display: "flex", gap: 8 }}>
              <Button
                hierarchy="tertiary"
                size="sm"
                onClick={() => setTableLoading((v) => !v)}
              >
                Toggle loading
              </Button>
              <Button
                hierarchy="tertiary"
                size="sm"
                onClick={() => setTableError((v) => !v)}
              >
                Toggle error
              </Button>
            </div>
          </Card.Footer>
        </Card>
      </Section>

      <Section title="RadioCardGrid">
        <RadioCardGrid
          items={RADIO_CARD_ITEMS}
          value={radioCardValue}
          onChange={setRadioCardValue}
        />
      </Section>

      <Section title="Tabs">
        <Row label="segmented">
          <Tabs
            variant="segmented"
            value={segmentedTab}
            onChange={setSegmentedTab}
            items={[
              { key: "all", label: "All", count: 12 },
              { key: "active", label: "Active", count: 9 },
              { key: "pending", label: "Pending", count: 3 },
            ]}
          />
        </Row>
        <Row label="underline">
          <Tabs
            variant="underline"
            value={underlineTab}
            onChange={setUnderlineTab}
            items={[
              { key: "overview", label: "Overview" },
              { key: "members", label: "Members" },
              { key: "billing", label: "Billing" },
            ]}
          />
        </Row>
        <Row label="segmented + disabled">
          <Tabs
            variant="segmented"
            value="setup"
            onChange={() => {}}
            items={[
              { key: "setup", label: "Setup" },
              { key: "design", label: "Design", disabled: true },
              { key: "install", label: "Install", disabled: true },
            ]}
          />
        </Row>
      </Section>

      <Section title="Pagination">
        <Row label="numbered">
          <Pagination
            variant="numbered"
            page={numberedPage}
            totalPages={12}
            onPageChange={setNumberedPage}
          />
        </Row>
        <Row label="simple">
          <Pagination
            variant="simple"
            page={simplePage}
            totalPages={5}
            onPageChange={setSimplePage}
          />
        </Row>
      </Section>

      <Section title="ProgressSteps">
        <div style={{ width: 320 }}>
          <ProgressSteps steps={PROGRESS_STEPS_EXAMPLE} />
        </div>
      </Section>

      <Section title="SidebarNav">
        <Row label="toggle">
          <Button
            hierarchy="secondaryGray"
            size="sm"
            onClick={() => setSidebarCollapsed((v) => !v)}
          >
            {sidebarCollapsed ? "Expand" : "Collapse"}
          </Button>
        </Row>
        <div className={styles.sidebarPreview}>
          <SidebarNav
            collapsed={sidebarCollapsed}
            brand={<strong>Acme</strong>}
            activeKey="members"
            sections={[
              {
                label: "Workspace",
                items: [
                  {
                    key: "dashboard",
                    label: "Dashboard",
                    icon: <Icon name="Settings01" />,
                  },
                  {
                    key: "members",
                    label: "Members",
                    icon: <Icon name="UserPlus01" />,
                    badge: 3,
                  },
                ],
              },
            ]}
            account={{
              name: "Maria Kotopoulou",
              email: "maria@company.com",
              initials: "MK",
            }}
          />
        </div>
      </Section>

      <Section title="DropdownMenu">
        <Row label="trigger">
          <DropdownMenu
            trigger={
              <IconButton
                icon={<Icon name="DotsVertical" />}
                label="More actions"
              />
            }
            items={[
              {
                key: "edit",
                label: "Edit details",
                icon: <Icon name="Settings01" />,
              },
              {
                key: "copy",
                label: "Copy invite link",
                icon: <Icon name="Copy01" />,
              },
              { type: "divider" },
              {
                key: "remove",
                label: "Remove from workspace",
                icon: <Icon name="Trash01" />,
                destructive: true,
              },
            ]}
          />
        </Row>
      </Section>

      <Section title="Modal">
        <Row label="trigger">
          <Button onClick={() => setModalOpen(true)}>Open modal</Button>
          <Button
            hierarchy="destructive"
            onClick={() => setDestructiveModalOpen(true)}
          >
            Open destructive modal
          </Button>
        </Row>
        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          icon={<Icon name="Settings01" />}
          title="Update role"
          footer={
            <>
              <Button
                hierarchy="secondaryGray"
                onClick={() => setModalOpen(false)}
              >
                Cancel
              </Button>
              <Button onClick={() => setModalOpen(false)}>Save changes</Button>
            </>
          }
        >
          Maria Kotopoulou will be updated from Viewer to Editor.
        </Modal>
        <Modal
          open={destructiveModalOpen}
          onClose={() => setDestructiveModalOpen(false)}
          variant="destructive"
          icon={<Icon name="Trash01" />}
          title="Remove member"
          footer={
            <>
              <Button
                hierarchy="secondaryGray"
                onClick={() => setDestructiveModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                hierarchy="destructive"
                onClick={() => setDestructiveModalOpen(false)}
              >
                Remove member
              </Button>
            </>
          }
        >
          Maria Kotopoulou will lose access to this workspace immediately. This
          can't be undone.
        </Modal>
      </Section>

      <Section title="Toast">
        <ToastTriggerRow />
      </Section>

      <Section title="Tooltip">
        <Row label="dark / light">
          <Tooltip content="Settings">
            <IconButton icon={<Icon name="Settings01" />} label="Settings" />
          </Tooltip>
          <Tooltip content="Delete member" variant="light" placement="bottom">
            <IconButton icon={<Icon name="Trash01" />} label="Delete" />
          </Tooltip>
        </Row>
      </Section>

      <Section title="EmptyState">
        <Row label="noData">
          <EmptyState
            icon={<Icon name="UserPlus01" />}
            title="No members yet"
            body="Invite your team to start collaborating."
            actions={[{ label: "Invite members", hierarchy: "primary" }]}
          />
        </Row>
        <Row label="noResults">
          <EmptyState
            icon={<Icon name="SearchLg" />}
            title="No members found"
            body={'No results for "maria". Try a different name or email.'}
            actions={[{ label: "Clear search", hierarchy: "secondaryGray" }]}
          />
        </Row>
        <Row label="error">
          <EmptyState
            icon={<Icon name="AlertCircle" />}
            variant="error"
            title="Couldn't load members"
            body="Something went wrong on our end."
            actions={[{ label: "Try again", hierarchy: "secondaryGray" }]}
          />
        </Row>
        <Row label="limitReached">
          <EmptyState
            icon={<Icon name="AlertTriangle" />}
            title="All seats are in use"
            body="You're using 5 of 5 seats on the Team plan."
            actions={[{ label: "Add seats", hierarchy: "primary" }]}
          />
        </Row>
      </Section>
    </div>
  );
};

const ThemeDemo = () => (
  <ToastProvider>
    <ThemeDemoContent />
  </ToastProvider>
);

export default ThemeDemo;
